/**
 * Lineage Resolution Service
 *
 * Computes and applies relationship dispositions when a lineage action retires
 * or transforms a source item. Per canon 07B.
 *
 * Two public surfaces:
 *   1. computeResolutionItems()  — pure function; builds the per-relationship
 *      resolution plan from the source item's active relationships.
 *   2. applyResolutions()        — async; executes the declared dispositions,
 *      creates new successor relationships, writes LineageRelationshipResolution
 *      records, and returns a summary.
 *
 * Disposition codes (canon 07B §2):
 *   dissolve  — relationship is terminated
 *   transfer  — relationship transferred to successor(s)
 *   no_action — source keeps its relationships (fork only)
 */

import { useItemRelationshipsStore } from '../stores/prioritization/itemRelationshipsStore';
import type {
  ItemRelationshipGroup,
  ItemRelationshipGroupMembership,
  ItemRelationshipEdge,
} from '../types/prioritization/itemRelationships';
import type { Item } from '../types/prioritization/lifecycle';

// ============================================================================
// Types
// ============================================================================

export type LineageAction = 'retire' | 'supersede' | 'split' | 'merge' | 'fork';

export type DispositionCode = 'dissolve' | 'transfer' | 'no_action';

/**
 * A single relationship on the source item that needs a disposition decision
 * before the lineage action can proceed.
 */
export interface ResolutionItem {
  /** Unique identifier within this resolution plan (= relationship id) */
  id: string;
  /** 'group' for cluster/package/exclusive/collection; 'edge' for dependency/aggregation/elaboration */
  source: 'group' | 'edge';
  /** Relationship type label */
  typeLabel: string;
  /** Direction or role label */
  roleLabel: string;
  /** Other item IDs involved in this relationship */
  otherItemIds: string[];
  /**
   * Default disposition per canon 07B §3.
   * null = operator MUST declare before proceeding (DECLARE disposition).
   */
  defaultDisposition: DispositionCode | null;
  /** Currently selected disposition (null until confirmed) */
  disposition: DispositionCode | null;
  /**
   * When true, this relationship actively blocks the lineage action
   * (e.g. Bloc membership). The dissolution readiness check (Gate 1) already
   * surfaces this; here it is included so the resolution step can explain why.
   */
  isBlocking: boolean;
  /** Transfer is available only when there is at least one successor. */
  canTransfer: boolean;
  /** Human-readable rationale for the default disposition. */
  note: string;
  /** Reason code for resolution record. */
  reasonCode: string;
}

export interface ResolutionPlan {
  items: ResolutionItem[];
  /** True when all items have a non-null disposition. */
  isComplete: boolean;
  /** True when any item is blocking (package membership etc.). */
  hasBlockers: boolean;
}

export interface ApplyResolutionsResult {
  dissolvedCount: number;
  transferredCount: number;
  errors: string[];
}

// ============================================================================
// Helpers — filter relationships for the source item
// ============================================================================

function getActiveGroupsForItem(
  itemId: string,
  groups: ItemRelationshipGroup[],
  memberships: ItemRelationshipGroupMembership[],
): ItemRelationshipGroup[] {
  const memberGroupIds = new Set(
    memberships
      .filter((m) => m.itemId === itemId)
      .map((m) => m.groupId),
  );
  return groups.filter(
    (g) => memberGroupIds.has(g.id) && g.status !== 'dissolved',
  );
}

function getActiveEdgesForItem(
  itemId: string,
  edges: ItemRelationshipEdge[],
): ItemRelationshipEdge[] {
  return edges.filter(
    (e) =>
      (e.sourceItemId === itemId || e.targetItemId === itemId) &&
      e.status !== 'dissolved',
  );
}

function itemTitle(id: string, getItemById: (id: string) => Item | undefined): string {
  return getItemById(id)?.title ?? id.slice(0, 12) + '…';
}

// ============================================================================
// computeResolutionItems
// ============================================================================

/**
 * Pure function. Given a source item and the current relationship state,
 * returns a resolution plan for all active relationships.
 *
 * Per canon 07B §3.
 */
export function computeResolutionItems(
  action: LineageAction,
  sourceItemId: string,
  groups: ItemRelationshipGroup[],
  memberships: ItemRelationshipGroupMembership[],
  edges: ItemRelationshipEdge[],
  successorCount: number,
  getItemById: (id: string) => Item | undefined,
): ResolutionItem[] {
  // Fork: source retains everything. No resolution needed.
  if (action === 'fork') return [];

  const hasSuccessor = successorCount > 0;
  const items: ResolutionItem[] = [];

  // ── Group-based relationships ──────────────────────────────────────────────

  const activeGroups = getActiveGroupsForItem(sourceItemId, groups, memberships);

  for (const group of activeGroups) {
    const otherMemberIds = memberships
      .filter((m) => m.groupId === group.id && m.itemId !== sourceItemId)
      .map((m) => m.itemId);

    if (group.type === 'cluster') {
      // Retire: BLOCK (must dissolve/eject first — canon 07B §3.1)
      // Supersede/Split/Merge: DECLARE (operator chooses what successors inherit)
      const isRetire = action === 'retire';
      items.push({
        id: group.id,
        source: 'group',
        typeLabel: 'Cluster',
        roleLabel: `Cluster member${group.subtype ? ` (${group.subtype})` : ''}`,
        otherItemIds: otherMemberIds,
        defaultDisposition: null, // null = BLOCK (retire) or DECLARE (others); both require operator action
        disposition: null,
        isBlocking: isRetire, // only BLOCK for retire; DECLARE for supersede/split/merge
        canTransfer: hasSuccessor && action === 'supersede',
        note: isRetire
          ? 'Cluster must be dissolved or this item ejected before retirement.'
          : 'Choose whether the successor(s) should join this cluster, or dissolve the cluster relationship.',
        reasonCode: 'LINEAGE_CLUSTER_DECLARE_REQUIRED',
      });
    } else if (group.type === 'package') {
      items.push({
        id: group.id,
        source: 'group',
        typeLabel: 'Package',
        roleLabel: `Package member${group.subtype ? ` (${group.subtype})` : ''}`,
        otherItemIds: otherMemberIds,
        defaultDisposition: null, // always BLOCK — must be dissolved manually first
        disposition: null,
        isBlocking: true,
        canTransfer: false, // canon 07B: successor must explicitly re-join
        note: 'Packages require all members to be alive. This Package must be dissolved before the lineage action can proceed.',
        reasonCode: 'LINEAGE_BLOC_BLOCK',
      });
    } else if (group.type === 'collection') {
      items.push({
        id: group.id,
        source: 'group',
        typeLabel: 'Collection',
        roleLabel: `Collection member${group.title ? ` — "${group.title}"` : ''}`,
        otherItemIds: [],
        defaultDisposition: 'dissolve', // canon 07B §7: collections always dissolve
        disposition: 'dissolve',
        isBlocking: false,
        canTransfer: false,
        note: 'Collection memberships are dissolved. Successors can be added to the same collections after the action.',
        reasonCode: 'LINEAGE_COLLECTION_SEEDING_OFFERED',
      });
    }
  }

  // ── Edge-based relationships ───────────────────────────────────────────────

  const activeEdges = getActiveEdgesForItem(sourceItemId, edges);

  for (const edge of activeEdges) {
    const isSource = edge.sourceItemId === sourceItemId;
    const otherItemId = isSource ? edge.targetItemId : edge.sourceItemId;

    if (edge.type === 'dependency') {
      const isUpstream = isSource; // source of a dependency edge = the blocker (upstream)
      const roleLabel = isUpstream
        ? `Dependency — this item blocks "${itemTitle(otherItemId, getItemById)}"${edge.subtype ? ` (${edge.subtype})` : ''}`
        : `Dependency — blocked by "${itemTitle(otherItemId, getItemById)}"${edge.subtype ? ` (${edge.subtype})` : ''}`;

      let defaultDisposition: DispositionCode | null;
      let note: string;

      if (action === 'retire') {
        defaultDisposition = 'dissolve';
        note = isUpstream
          ? 'This item blocks another. Dissolving will unblock it. A resolution record notifies the downstream item.'
          : 'This item was blocked by another. Dissolving removes the constraint.';
      } else if (action === 'supersede') {
        defaultDisposition = 'transfer';
        note = isUpstream
          ? 'The successor will inherit this blocking relationship.'
          : 'The successor will inherit the blocked-by constraint.';
      } else if (action === 'split') {
        // Downstream (blocked) transfers to all; upstream (blocker) must be declared
        defaultDisposition = isUpstream ? null : 'transfer';
        note = isUpstream
          ? 'Which successor should own this upstream blocking position? At most one can block — or dissolve.'
          : 'All successors inherit this blocked-by constraint (each still needs the prerequisite).';
      } else {
        // merge
        defaultDisposition = 'transfer';
        note = 'The merged item inherits this dependency relationship.';
      }

      items.push({
        id: edge.id,
        source: 'edge',
        typeLabel: 'Dependency',
        roleLabel,
        otherItemIds: [otherItemId],
        defaultDisposition,
        disposition: defaultDisposition,
        isBlocking: false,
        canTransfer: hasSuccessor && action !== 'retire',
        note,
        reasonCode: 'LINEAGE_DEPENDENCY_UPSTREAM_DISSOLVED',
      });
    } else if (edge.type === 'aggregation') {
      const isParent = isSource; // source = parent in aggregation
      const roleLabel = isParent
        ? `Aggregation parent — "${itemTitle(otherItemId, getItemById)}" is a child`
        : `Aggregation child — parent is "${itemTitle(otherItemId, getItemById)}"`;

      let defaultDisposition: DispositionCode | null;
      let note: string;

      if (action === 'retire') {
        defaultDisposition = isParent ? 'dissolve' : 'dissolve';
        note = isParent
          ? `Child "${itemTitle(otherItemId, getItemById)}" will become a root item.`
          : 'This item is removed from the parent aggregation.';
      } else if (action === 'supersede') {
        defaultDisposition = 'transfer';
        note = isParent
          ? 'All aggregation children will be reassigned to the successor.'
          : 'The aggregation edge will be updated to the successor.';
      } else if (action === 'split') {
        defaultDisposition = isParent ? null : 'transfer';
        note = isParent
          ? 'Redistribute this child to one of the successors, or dissolve (child becomes root).'
          : 'All successors become children of the parent, unless you declare otherwise.';
      } else {
        // merge
        defaultDisposition = 'transfer';
        note = isParent
          ? 'The merged item adopts all aggregation children.'
          : 'The merged item becomes the child.';
      }

      items.push({
        id: edge.id,
        source: 'edge',
        typeLabel: 'Aggregation',
        roleLabel,
        otherItemIds: [otherItemId],
        defaultDisposition,
        disposition: defaultDisposition,
        isBlocking: false,
        canTransfer: hasSuccessor && action !== 'retire',
        note,
        reasonCode: isParent
          ? 'LINEAGE_AGGREGATION_CHILDREN_ORPHANED'
          : 'LINEAGE_TRANSFER_APPLIED',
      });
    } else if (edge.type === 'elaboration') {
      const isParent = isSource; // source = parent in elaboration
      const roleLabel = isParent
        ? `Elaboration parent — "${itemTitle(otherItemId, getItemById)}" is an option`
        : `Elaboration option — need statement is "${itemTitle(otherItemId, getItemById)}"`;

      let defaultDisposition: DispositionCode | null;
      let note: string;

      if (action === 'retire') {
        defaultDisposition = 'dissolve';
        note = isParent
          ? `Option "${itemTitle(otherItemId, getItemById)}" will be orphaned.`
          : 'This option is removed from the elaboration.';
      } else if (action === 'supersede') {
        defaultDisposition = 'transfer';
        note = isParent
          ? 'All elaboration options will be reassigned to the successor.'
          : 'The elaboration edge will be updated to the successor.';
      } else if (action === 'split') {
        defaultDisposition = isParent ? null : 'transfer';
        note = isParent
          ? 'Redistribute this option to one of the successors, or dissolve.'
          : 'All successors become options of the need statement.';
      } else {
        // merge
        defaultDisposition = 'transfer';
        note = isParent
          ? 'The merged item adopts all elaboration options.'
          : 'The merged item becomes the option.';
      }

      items.push({
        id: edge.id,
        source: 'edge',
        typeLabel: 'Elaboration',
        roleLabel,
        otherItemIds: [otherItemId],
        defaultDisposition,
        disposition: defaultDisposition,
        isBlocking: false,
        canTransfer: hasSuccessor && action !== 'retire',
        note,
        reasonCode: isParent
          ? 'LINEAGE_ELABORATION_OPTIONS_ORPHANED'
          : 'LINEAGE_TRANSFER_APPLIED',
      });
    } else if (edge.type === 'prerequisite') {
      // Mirrors Dependency: source = the enabler (prerequisite); target = the enabled item.
      const isEnabler = isSource;
      const roleLabel = isEnabler
        ? `Prerequisite — this item enables "${itemTitle(otherItemId, getItemById)}"${edge.subtype ? ` (${edge.subtype})` : ''}`
        : `Prerequisite — enabled by "${itemTitle(otherItemId, getItemById)}"${edge.subtype ? ` (${edge.subtype})` : ''}`;

      let defaultDisposition: DispositionCode | null;
      let note: string;
      let reasonCode: string;

      if (action === 'retire') {
        defaultDisposition = 'dissolve';
        note = isEnabler
          ? 'This prerequisite is dissolved. Enabled items become unblocked. A resolution record notifies them.'
          : 'This item\'s prerequisite requirement is dissolved.';
        reasonCode = isEnabler ? 'LINEAGE_PREREQUISITE_UPSTREAM_DISSOLVED' : 'LINEAGE_TRANSFER_APPLIED';
      } else if (action === 'supersede') {
        defaultDisposition = 'transfer';
        note = isEnabler
          ? 'The successor inherits this enabling position.'
          : 'The successor inherits this prerequisite requirement.';
        reasonCode = 'LINEAGE_TRANSFER_APPLIED';
      } else if (action === 'split') {
        // Enabler role must be declared (which successor holds it?); enabled transfers to all.
        defaultDisposition = isEnabler ? null : 'transfer';
        note = isEnabler
          ? 'Which successor should hold the enabling position? At most one can — or dissolve.'
          : 'All successors inherit this prerequisite requirement.';
        reasonCode = isEnabler ? 'LINEAGE_PREREQUISITE_UPSTREAM_DISSOLVED' : 'LINEAGE_TRANSFER_APPLIED';
      } else {
        // merge
        defaultDisposition = 'transfer';
        note = 'The merged item inherits this prerequisite relationship.';
        reasonCode = 'LINEAGE_TRANSFER_APPLIED';
      }

      items.push({
        id: edge.id,
        source: 'edge',
        typeLabel: 'Prerequisite',
        roleLabel,
        otherItemIds: [otherItemId],
        defaultDisposition,
        disposition: defaultDisposition,
        isBlocking: false,
        canTransfer: hasSuccessor && action !== 'retire',
        note,
        reasonCode,
      });
    } else if (edge.type === 'breakdown') {
      // Mirrors Aggregation exactly: source = parent (declares parts); target = child (the part).
      const isParent = isSource;
      const roleLabel = isParent
        ? `Breakdown parent — "${itemTitle(otherItemId, getItemById)}" is a declared part`
        : `Breakdown part — parent is "${itemTitle(otherItemId, getItemById)}"`;

      let defaultDisposition: DispositionCode | null;
      let note: string;
      let reasonCode: string;

      if (action === 'retire') {
        defaultDisposition = 'dissolve';
        note = isParent
          ? `Declared part "${itemTitle(otherItemId, getItemById)}" will become a root item.`
          : 'This item is removed from the parent breakdown.';
        reasonCode = isParent ? 'LINEAGE_BREAKDOWN_CHILDREN_ORPHANED' : 'LINEAGE_TRANSFER_APPLIED';
      } else if (action === 'supersede') {
        defaultDisposition = 'transfer';
        note = isParent
          ? 'All declared parts will be reassigned to the successor.'
          : 'The breakdown edge will be updated to the successor.';
        reasonCode = 'LINEAGE_TRANSFER_APPLIED';
      } else if (action === 'split') {
        defaultDisposition = isParent ? null : 'transfer';
        note = isParent
          ? 'Redistribute this declared part to one of the successors, or dissolve (part becomes root).'
          : 'All successors become declared parts of the original parent.';
        reasonCode = isParent ? 'LINEAGE_BREAKDOWN_CHILDREN_ORPHANED' : 'LINEAGE_TRANSFER_APPLIED';
      } else {
        // merge
        defaultDisposition = 'transfer';
        note = isParent
          ? 'The merged item adopts all declared parts.'
          : 'The merged item becomes the declared part.';
        reasonCode = 'LINEAGE_TRANSFER_APPLIED';
      }

      items.push({
        id: edge.id,
        source: 'edge',
        typeLabel: 'Breakdown',
        roleLabel,
        otherItemIds: [otherItemId],
        defaultDisposition,
        disposition: defaultDisposition,
        isBlocking: false,
        canTransfer: hasSuccessor && action !== 'retire',
        note,
        reasonCode,
      });
    } else if (edge.type === 'contains') {
      // Passive reporting hierarchy: source = parent, target = child (canon 07B §3).
      const isParent = isSource;
      const roleLabel = isParent
        ? `Contains parent — "${itemTitle(otherItemId, getItemById)}" is a child`
        : `Contains child — parent is "${itemTitle(otherItemId, getItemById)}"`;

      let defaultDisposition: DispositionCode | null;
      let note: string;
      let reasonCode: string;

      if (action === 'retire') {
        defaultDisposition = 'dissolve';
        note = isParent
          ? `Child "${itemTitle(otherItemId, getItemById)}" will become a root item in the reporting hierarchy.`
          : 'This item is removed from its Contains parent.';
        reasonCode = isParent ? 'LINEAGE_CONTAINS_CHILDREN_ORPHANED' : 'LINEAGE_CONTAINS_TRANSFER_APPLIED';
      } else if (action === 'supersede') {
        defaultDisposition = 'transfer';
        note = isParent
          ? 'All Contains children will be reassigned to the successor by default. Override to Dissolve to orphan children as roots.'
          : 'The Contains edge will be updated to the successor.';
        reasonCode = 'LINEAGE_CONTAINS_TRANSFER_APPLIED';
      } else if (action === 'split') {
        defaultDisposition = isParent ? null : 'transfer';
        note = isParent
          ? 'Assign each Contains child to one successor, or dissolve (child becomes a root item).'
          : 'Successor items remain under the unchanged Contains parent unless you override.';
        reasonCode = isParent
          ? 'LINEAGE_CONTAINS_REDISTRIBUTION_REQUIRED'
          : 'LINEAGE_CONTAINS_TRANSFER_APPLIED';
      } else {
        defaultDisposition = 'transfer';
        note = isParent
          ? 'The merged item adopts all Contains children from all sources (de-duplicated).'
          : 'The merged item becomes the child in the Contains relationship.';
        reasonCode = 'LINEAGE_CONTAINS_TRANSFER_APPLIED';
      }

      items.push({
        id: edge.id,
        source: 'edge',
        typeLabel: 'Contains',
        roleLabel,
        otherItemIds: [otherItemId],
        defaultDisposition,
        disposition: defaultDisposition,
        isBlocking: false,
        canTransfer: hasSuccessor && action !== 'retire',
        note,
        reasonCode,
      });
    }
    // Note: 'related' edge type is not in the canonical 7 — skipped.
  }

  return items;
}

// ============================================================================
// applyResolutions
// ============================================================================

/**
 * Applies confirmed dispositions for a completed lineage action.
 *
 * Must be called AFTER the lineage event record has been created,
 * so that `lineageEventId` is available.
 *
 * Per canon 07B §4 (Resolution Records).
 */
export async function applyResolutions({
  lineageEventId,
  sourceItemId,
  successorItemIds,
  resolutionItems,
}: {
  lineageEventId: string;
  sourceItemId: string;
  successorItemIds: string[];
  resolutionItems: ResolutionItem[];
}): Promise<ApplyResolutionsResult> {
  const store = useItemRelationshipsStore.getState();
  const errors: string[] = [];
  let dissolvedCount = 0;
  let transferredCount = 0;

  for (const item of resolutionItems) {
    const disposition = item.disposition ?? item.defaultDisposition ?? 'dissolve';

    // ── Dissolve ────────────────────────────────────────────────────────────
    if (disposition === 'dissolve') {
      let success = false;
      if (item.source === 'group') {
        // Remove source item from the group (not full dissolve — other members may remain)
        success = await store.removeGroupMember(item.id, sourceItemId);
      } else {
        success = await store.dissolveEdge(item.id);
      }

      if (!success) {
        errors.push(`Failed to dissolve ${item.typeLabel} relationship ${item.id}`);
        continue;
      }
      dissolvedCount++;

      // Write resolution record
      await store.createLineageRelationshipResolution({
        lineageEventId,
        relationshipId: item.id,
        relationshipSource: item.source,
        resolutionAction: 'dissolve',
        resolutionNote: JSON.stringify({
          dispositionCode: 'dissolve',
          sourceAction: 'lineage',
          reasonCode: item.reasonCode,
          sourceItemId,
        }),
      });
    }

    // ── Transfer ────────────────────────────────────────────────────────────
    else if (disposition === 'transfer' && successorItemIds.length > 0) {
      const targets = successorItemIds; // transfer to all successors

      for (const targetItemId of targets) {
        let success = false;

        if (item.source === 'group') {
          // Add successor to the group
          success = await store.addGroupMembers(item.id, [targetItemId]);
        } else {
          // Recreate the edge pointing to/from the successor instead of source
          const edges = store.edges;
          const originalEdge = edges.find((e) => e.id === item.id);
          if (originalEdge) {
            const newEdge = {
              ...originalEdge,
              sourceItemId: originalEdge.sourceItemId === sourceItemId ? targetItemId : originalEdge.sourceItemId,
              targetItemId: originalEdge.targetItemId === sourceItemId ? targetItemId : originalEdge.targetItemId,
              status: 'active' as const,
            };
            const created = await store.createEdge({
              type: newEdge.type,
              sourceItemId: newEdge.sourceItemId,
              targetItemId: newEdge.targetItemId,
              status: newEdge.status,
              subtype: newEdge.subtype,
              magnitude: newEdge.magnitude, // a Carries edge keeps its share (migration 968)
              directionality: newEdge.directionality,
              declaredMode: newEdge.declaredMode,
              note: newEdge.note,
              rationale: newEdge.rationale,
              createdBy: newEdge.createdBy,
              workspaceId: newEdge.workspaceId,
            });
            success = created !== null;
          }
        }

        if (!success) {
          errors.push(`Failed to transfer ${item.typeLabel} to successor ${targetItemId}`);
          continue;
        }
        transferredCount++;

        // Write transfer resolution record (queryable by successor via targetItemId)
        await store.createLineageRelationshipResolution({
          lineageEventId,
          relationshipId: item.id,
          relationshipSource: item.source,
          resolutionAction: 'transfer',
          resolutionNote: JSON.stringify({
            dispositionCode: 'transfer',
            sourceAction: 'lineage',
            reasonCode: item.reasonCode,
            sourceItemId,
            targetItemId,
          }),
        });
      }

      // Dissolve the original after transferring
      if (item.source === 'group') {
        await store.removeGroupMember(item.id, sourceItemId);
      } else {
        await store.dissolveEdge(item.id);
      }
      dissolvedCount++;
    }
  }

  return { dissolvedCount, transferredCount, errors };
}

// ============================================================================
// buildResolutionPlan — convenience wrapper
// ============================================================================

export function buildResolutionPlan(items: ResolutionItem[]): ResolutionPlan {
  const hasBlockers = items.some((i) => i.isBlocking && i.defaultDisposition === null);
  const isComplete = !hasBlockers && items.every((i) => i.disposition !== null || i.defaultDisposition !== null);
  return { items, isComplete, hasBlockers };
}
